import { Link, useParams } from "react-router-dom";
import { Figure } from "../components/Figure";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { DISTINGUISHED, getAlumnus } from "../lib/alumni";
import { useScrollPastHero } from "../lib/useScrollPastHero";

export default function AlumniProfile() {
  const { slug = "" } = useParams<{ slug: string }>();
  const a = getAlumnus(slug);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (!a) {
    return (
      <section className="alum-hero">
        <div className="container">
          <div className="ah-eyebrow">404 · Alumni</div>
          <h1 className="ah-name">Profile not found.</h1>
          <p>
            <Link to={ROUTES.alumni}>← Back to alumni</Link>
          </p>
        </div>
      </section>
    );
  }

  const others = DISTINGUISHED.filter((x) => x.slug !== a.slug).slice(0, 3);

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag={a.year ? `CLASS OF ${a.year}` : a.department.toUpperCase()}
        title={a.name}
        back={{ label: "All alumni", to: ROUTES.alumni }}
      />
      <section className="alum-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb" style={{ color: "var(--text-muted)" }}>
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.alumni}>Alumni</Link>
            <span aria-hidden>/</span>
            <span>Distinguished</span>
          </nav>

          <div className="ah-grid">
            <div className="ah-portrait">
              <Figure media={a.photo} fallbackLabel={"PORTRAIT · " + a.name.toUpperCase()} aspect="3/4" />
            </div>
            <div>
              <div className="ah-eyebrow">{a.year ? `Class of ${a.year} · ` : ""}{a.department}</div>
              <h1 className="ah-name">{a.name}</h1>
              <div className="ah-role">{a.role}</div>
              {a.quote && <div className="ah-quote">"{a.quote}"</div>}
            </div>
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              {a.story ? (
                <>
                  <h2>The story</h2>
                  {a.story.map((p, i) => <p key={i}>{p}</p>)}
                </>
              ) : a.note ? (
                <p>{a.note}</p>
              ) : null}

              {a.journey && (
                <>
                  <h2>Career arc</h2>
                  <ul className="alum-journey">
                    {a.journey.map((j, i) => (
                      <li key={i}>
                        <div className="yr">{j.year}</div>
                        <div className="tx">{j.what}</div>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {a.advice && (
                <div className="alum-advice">
                  <span className="lbl">Advice for current students</span>
                  "{a.advice}"
                </div>
              )}
            </div>

            <aside className="detail-meta-card is-accented">
              {a.year && (
                <div className="dmc-block">
                  <div className="dmc-label">Class of</div>
                  <div className="dmc-value">{a.year}</div>
                </div>
              )}
              <div className="dmc-block">
                <div className="dmc-label">Department</div>
                <div className="dmc-value">{a.department}</div>
              </div>
              {a.bietYears && (
                <div className="dmc-block">
                  <div className="dmc-label">Years at BIET</div>
                  <div className="dmc-value">{a.bietYears.in} – {a.bietYears.out}</div>
                </div>
              )}
              {a.basedIn && (
                <div className="dmc-block">
                  <div className="dmc-label">Currently based in</div>
                  <div className="dmc-value">{a.basedIn}</div>
                </div>
              )}
              <div className="dmc-block">
                <div className="dmc-label">Role</div>
                <div className="dmc-value">{a.role}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Network</div>
                <div className="dmc-value">
                  <Link to={ROUTES.alumni}>Visit alumni network →</Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="event-related">
          <div className="container">
            <div className="er-head">
              <div className="kicker">More distinguished alumni</div>
              <h2>Other paths from these workshops</h2>
            </div>
            <div className="er-grid">
              {others.map((o) => (
                <Link key={o.slug} to={`${ROUTES.alumni}/${o.slug}`} className="er-card">
                  <div className="er-date">
                    <span className="er-day">{o.year ? `'${o.year.slice(2)}` : "—"}</span>
                    <span className="er-month">CLASS</span>
                  </div>
                  <div className="er-body">
                    <span className="er-tag">{o.department}</span>
                    <h3>{o.name}</h3>
                    <span className="er-venue">{o.role}</span>
                  </div>
                  <span className="er-arr" aria-hidden>→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
